import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { cn, message } from "@/lib/utils";
import { getPriorityBadgeClass } from "@/lib/chart";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";
import type { AIRecommendation } from "@/schema/brand-os/brand-intel";

function RecommendationCard({ item }: { item: AIRecommendation }) {
  const priorityKey = item.priority.toLowerCase();
  const priorityLabel = priorityKey.charAt(0).toUpperCase() + priorityKey.slice(1);
  return (
    <Card className="flex flex-col gap-2 rounded-xl border border-blue-200/50 bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-3">
          <Badge variant="secondary" className={cn(getPriorityBadgeClass(priorityKey))}>
            {priorityLabel}
          </Badge>
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-semibold text-foreground">{item.title}</p>
            <p className="text-xs text-muted-foreground leading-relaxed">{item.description}</p>
          </div>
        </div>
        <p className="shrink-0 text-xs font-semibold text-success">+{Math.round(item.points)} pts</p>
      </div>
    </Card>
  );
}

function RecommendationsGrid({ items }: { items: AIRecommendation[] }) {
  if (!items.length) return <LockedContent message={message} className="mb-10" />;
  return (
    <div className={cn("grid grid-cols-1 gap-3", items.length === 1 ? "md:grid-cols-1" : "md:grid-cols-2")}>
      {items.map((item, index) => (
        <RecommendationCard key={index} item={item} />
      ))}
    </div>
  );
}

function RecommendationsContent({ status, items }: { status: QueryStatus; items: AIRecommendation[] }) {
  switch (status) {
    case "pending":
      return <RecommendationsSkeleton />;
    case "error":
      return <LockedContent message={message} className="mb-10" />;
    case "success":
      return <RecommendationsGrid items={items} />;
  }
}

export function AIRecommendations() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchBrandAuditAIRecommendations());

  const items = data?.recommendations ?? [];
  const totalActions = data?.total_actions ?? items.length;

  return (
    <Card className={items.length > 0 ? "border border-[#DDD6FE]/30 bg-gradient-to-br from-[#F5F3FF66] via-[#EFF6FF4D] to-[#FFFFFF]" : ""}>
      <CardHeader className="flex flex-row items-center gap-2">
        <div className="rounded-md bg-violet-100 p-2">
          <Sparkles className="size-5 text-violet-600" strokeWidth={2.5} />
        </div>
        <CardTitle className="text-base font-semibold">AI Recommendations</CardTitle>
        {items.length > 0 && (
          <Badge variant="secondary" className="bg-violet-100/40 text-violet-600">
            {totalActions} actions
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        <RecommendationsContent status={status} items={items} />
      </CardContent>
    </Card>
  );
}

function RecommendationsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}
    </div>
  );
}
